import { useState } from "react";
import { isLiveHeader, useSettings, useTrading, type ConnectionView, type Me } from "@helm/core";
import { formatMoney, pnlTone } from "@helm/ui";
import { Home } from "../mobile/Home";
import { Markets } from "../mobile/Markets";
import { Trade } from "../mobile/Trade";
import { Positions } from "../mobile/Positions";
import { Account } from "../mobile/Account";
import { headlineOf, useAccountView } from "../viewmodel/account";
import { NetworkStatusIndicator } from "../topbar/TopBar";
import { RithmicAccountControl } from "../topbar/RithmicAccount";
import { SimBadge } from "../trading/SimBadge";
import { Icon, type IconName } from "../components/Icon";
import { ToastHost } from "../components/Toasts";
import { TradingHosts } from "../trading/hosts";
import { FlattenWaitingBanner } from "../trading/FlattenWaitingBanner";
import { UnconfirmedPositionsBanner } from "../trading/UnconfirmedPositionsBanner";
import { ConnectionBanner } from "./ConnectionBanner";
import { CoachSlot } from "../coach/coachGate";
import { PlantDots } from "./PlantDots";

const TABS: ReadonlyArray<readonly [Tab, IconName]> = [
  ["Home", "home"],
  ["Markets", "markets"],
  ["Trade", "chart"],
  ["Positions", "positions"],
  ["Account", "user"],
];
type Tab = "Home" | "Markets" | "Trade" | "Positions" | "Account";

/**
 * Five-tab mobile frame (Tradesea mobile: Home, Markets, Trade, Positions, Account) with the
 * persistent Open Orders + UP&L strip (SPEC §17.7 status strip; tap → Positions). The Rithmic
 * account control and the trading-environment badge head every tab (switch accounts there;
 * "Rithmic logins…" opens the Account tab).
 */
export function MobileShell({ me, connection }: { me: Me; connection: ConnectionView }): React.JSX.Element {
  const [tab, setTab] = useState<Tab>("Home");
  const account = useAccountView();
  const view = headlineOf(account);
  const sessionEnv = useTrading((t) => t.session?.environment);
  const live = isLiveHeader(sessionEnv, account.allSelected ? null : (account.active?.environment ?? null));
  const privacy = useSettings((s) => s.settings.privacy);
  return (
    <div className="shell shell-mobile">
      {/* The banners sit above the scroller, below the status bar / notch (audit S-13): they never
          scroll, so they cannot slide under the safe area in the installed PWA. */}
      <div className="mobile-top">
        {/* Is this live? Gateway and Rithmic session state first (CB5), then unconfirmed positions. */}
        <div className="shell-banners mobile-banners">
          <ConnectionBanner connection={connection} />
          <UnconfirmedPositionsBanner />
          <FlattenWaitingBanner />
          <CoachSlot />
        </div>
        <main className="mobile-main" aria-labelledby="mobile-title">
          {/* The Rithmic account and its environment first, as on desktop (top left). */}
          <div
            className={live ? "mobile-account-row mobile-live" : "mobile-account-row"}
            role="group"
            aria-label="Rithmic account and trading environment"
            data-live={live ? "1" : undefined}
          >
            <RithmicAccountControl
              view={account}
              privacy={privacy}
              compact
              onOpenRithmic={() => {
                setTab("Account");
              }}
            />
            <SimBadge compact />
          </div>
          <header className="mobile-head">
            <h1 className="mobile-title" id="mobile-title">
              {tab}
            </h1>
            <NetworkStatusIndicator connection={connection} />
          </header>
          {tab === "Home" && <Home connection={connection} />}
          {tab === "Markets" && <Markets />}
          {tab === "Trade" && <Trade />}
          {tab === "Positions" && <Positions />}
          {tab === "Account" && <Account me={me} connection={connection} />}
        </main>
      </div>
      <button
        type="button"
        className="status-strip"
        onClick={() => {
          setTab("Positions");
        }}
        aria-label="Open orders and unrealized P&L — show positions"
      >
        <span>
          Open Orders <span className="num">{view.workingOrders ?? "—"}</span>
        </span>
        <span className="spacer" />
        <span>
          UP&amp;L{" "}
          <span className={`num tone-${pnlTone(view.unrealizedPnl)}`}>
            {formatMoney(view.unrealizedPnl, { privacy, signed: true })}
          </span>
        </span>
        <PlantDots connected={connection.status === "connected"} compact />
        <NetworkStatusIndicator connection={connection} compact />
      </button>
      <nav className="tabbar" aria-label="Primary">
        {TABS.map(([t, icon]) => (
          <button
            key={t}
            type="button"
            className={t === tab ? "tabbtn active" : "tabbtn"}
            aria-current={t === tab ? "page" : undefined}
            onClick={() => {
              setTab(t);
            }}
          >
            <Icon name={icon} size={20} />
            <span>{t}</span>
          </button>
        ))}
      </nav>
      {/* Order outcomes and gateway notices, and the confirm surfaces the order actions await
          (order confirm, stop-through-market warning, Flatten ALL, exit progress): without them
          a confirm would never resolve on the phone. */}
      <TradingHosts />
      <ToastHost />
    </div>
  );
}
